import * as THREE from 'three'

interface Tracer {
  mesh: THREE.Mesh
  material: THREE.MeshBasicMaterial
  life: number
}

const LIFE = 0.1 // seconds visible (a quick streak)
const THICKNESS = 2.4 // world-unit cross-section of the streak
const MAX_LEN = 1600 // cap very long shots so they read as a streak, not a wall
const AXIS_Y = new THREE.Vector3(0, 1, 0)
const AXIS_X = new THREE.Vector3(1, 0, 0)

/** A bright yellow tracer streak that glows down a bullet's path, so players can
 * see where shots come from. Pooled additive billboarded quads, oriented along
 * the segment and turned to face the camera. */
export default class Tracers {
  private readonly pool: Tracer[] = []
  private next = 0
  private readonly dir = new THREE.Vector3()
  private readonly mid = new THREE.Vector3()
  private readonly toCam = new THREE.Vector3()
  private readonly up = new THREE.Vector3()
  private readonly normal = new THREE.Vector3()
  private readonly basis = new THREE.Matrix4()

  constructor(scene: THREE.Scene, size = 24) {
    const texture = makeStreakTexture()
    const geometry = new THREE.PlaneGeometry(1, 1)
    for (let i = 0; i < size; i++) {
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        color: 0xffd23a,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      })
      const mesh = new THREE.Mesh(geometry, material)
      mesh.visible = false
      mesh.frustumCulled = false
      scene.add(mesh)
      this.pool.push({ mesh, material, life: 0 })
    }
  }

  /** Streak from `from` toward `to`, billboarded to face `cameraPos`. */
  public spawn(
    from: THREE.Vector3,
    to: THREE.Vector3,
    cameraPos: THREE.Vector3,
  ): void {
    this.dir.subVectors(to, from)
    let len = this.dir.length()
    if (len < 1) return
    this.dir.divideScalar(len)
    if (len > MAX_LEN) len = MAX_LEN
    this.mid.copy(from).addScaledVector(this.dir, len * 0.5)

    // Billboard around the segment axis: keep local +X along the bullet path and
    // rotate so the quad's face points at the camera.
    this.toCam.subVectors(cameraPos, this.mid)
    this.up.crossVectors(this.dir, this.toCam)
    if (this.up.lengthSq() < 1e-6) {
      // Looking straight down the tracer — any perpendicular works.
      this.up.crossVectors(this.dir, AXIS_Y)
      if (this.up.lengthSq() < 1e-6) this.up.crossVectors(this.dir, AXIS_X)
    }
    this.up.normalize()
    this.normal.crossVectors(this.dir, this.up).normalize()
    this.basis.makeBasis(this.dir, this.up, this.normal)

    const t = this.pool[this.next]
    this.next = (this.next + 1) % this.pool.length
    t.mesh.position.copy(this.mid)
    t.mesh.quaternion.setFromRotationMatrix(this.basis)
    t.mesh.scale.set(len, THICKNESS, 1)
    t.mesh.visible = true
    t.material.opacity = 1
    t.life = LIFE
  }

  public update(dt: number): void {
    for (const t of this.pool) {
      if (t.life <= 0) continue
      t.life -= dt
      if (t.life <= 0) {
        t.mesh.visible = false
        continue
      }
      t.material.opacity = t.life / LIFE
    }
  }
}

/** A soft glowing lozenge: bright core fading to transparent at the ends and
 * top/bottom, so the stretched quad reads as a streak of light. */
function makeStreakTexture(): THREE.Texture {
  const w = 64
  const h = 16
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return new THREE.Texture()
  const img = ctx.createImageData(w, h)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const fx = 1 - Math.abs((x / (w - 1)) * 2 - 1)
      const fy = 1 - Math.abs((y / (h - 1)) * 2 - 1)
      const a = Math.pow(fx, 1.5) * Math.pow(fy, 2.5)
      const i = (y * w + x) * 4
      img.data[i] = 255
      img.data[i + 1] = 240
      img.data[i + 2] = 180
      img.data[i + 3] = Math.round(a * 255)
    }
  }
  ctx.putImageData(img, 0, 0)
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.needsUpdate = true
  return tex
}
